/**
 * [HF4.5] BetweenRoundsCountdownModal - small in-context modal that fires at
 * each snake-draft round boundary (rounds 2-N). Round 1 has its own
 * 10-second lobby countdown (DraftCountdown in pages/DraftLobby.jsx) - this
 * modal does NOT fire for round 1.
 *
 * Mounted by: SnakeStandaloneDraftBoard.jsx (founder spec - board only).
 * Not mounted in the draft room or dashboard. Future scope (TD-47): once the
 * snake dashboard is overhauled to mirror auction, this modal moves into the
 * center activity panel surface.
 *
 * Visual style: mirrors the auction "On the Clock" card from
 * StandaloneDraftBoard.jsx:494-524 - orange-bordered rounded card, NOT a
 * full-screen takeover. Backdrop dim with light blur. Round heading in
 * orange uppercase, big numeric ticks below.
 *
 * Lifecycle (~3.4s total):
 *   t=0    : mount, render "3"
 *   t=1s   : render "2"
 *   t=2s   : render "1"
 *   t=3s   : onComplete() - parent unmounts
 *
 * Skip button forces onSkip + onComplete with no further ticks.
 * ESC key acts as Skip.
 */
import { useEffect, useState, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";

export default function BetweenRoundsCountdownModal({
  round,
  onComplete,
  onSkip,
  isDark = true,
}) {
  const [tick, setTick] = useState(3);
  const timersRef = useRef([]);
  const completedRef = useRef(false);

  useEffect(() => {
    const schedule = (delay, fn) => {
      const id = setTimeout(fn, delay);
      timersRef.current.push(id);
      return id;
    };

    schedule(1000, () => setTick(2));
    schedule(2000, () => setTick(1));
    schedule(3000, () => {
      if (!completedRef.current) {
        completedRef.current = true;
        onComplete?.();
      }
    });

    return () => {
      timersRef.current.forEach(clearTimeout);
      timersRef.current = [];
    };
    // onComplete intentionally not a dep - modal owns its own lifecycle.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSkip = () => {
    timersRef.current.forEach(clearTimeout);
    timersRef.current = [];
    if (!completedRef.current) {
      completedRef.current = true;
      onSkip?.();
      onComplete?.();
    }
  };

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") handleSkip(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auction "On the Clock" palette - kept identical for visual continuity
  // between board surfaces.
  const accent = "#FF6B00";
  const titleColor = isDark ? "#F5F5F5" : "#0A0A0A";
  const cardBg = isDark ? "#0F0F12" : "#FFFFFF";
  const overlayBg = isDark ? "rgba(0,0,0,0.55)" : "rgba(20,20,20,0.35)";

  return (
    <AnimatePresence>
      <motion.div
        key={`between-rounds-${round}`}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.18 }}
        className="absolute inset-0 z-40 flex items-center justify-center"
        style={{ backdropFilter: "blur(4px)", background: overlayBg }}
        data-testid="between-rounds-countdown-modal"
      >
        <motion.div
          initial={{ scale: 0.94, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.94, opacity: 0 }}
          transition={{ duration: 0.22, ease: [0.25, 0.1, 0.25, 1] }}
          className="relative text-center px-10 py-8 rounded-2xl border"
          style={{
            background: cardBg,
            borderColor: `${accent}55`,
            boxShadow: `0 0 60px ${accent}26, 0 20px 48px rgba(0,0,0,0.45)`,
          }}
        >
          <p
            className="font-display font-black text-xs uppercase tracking-[0.4em] mb-3"
            style={{ color: accent }}
            data-testid="between-rounds-heading"
          >
            Round {round} starts in
          </p>
          <p
            key={String(tick)}
            className="font-display font-black leading-none"
            style={{
              color: titleColor,
              fontSize: "clamp(5rem, 9vw, 8rem)",
              textShadow: `0 0 24px ${accent}33`,
              animation: "pgf-brc-pop 0.32s ease-out",
            }}
            data-testid={`between-rounds-tick-${tick}`}
          >
            {tick}
          </p>

          <button
            type="button"
            onClick={handleSkip}
            data-testid="between-rounds-skip"
            className="mt-6 px-3 py-1.5 rounded-md font-display font-bold text-[10px] uppercase tracking-wider transition-all"
            style={{
              color: "rgba(255,255,255,0.55)",
              border: "1px solid rgba(255,255,255,0.18)",
              background: "rgba(0,0,0,0.35)",
            }}
            aria-label="Skip countdown"
          >
            Skip ↦
          </button>

          <style>{`
            @keyframes pgf-brc-pop {
              0%   { transform: scale(0.55); opacity: 0; }
              60%  { transform: scale(1.12); opacity: 1; }
              100% { transform: scale(1); opacity: 1; }
            }
          `}</style>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
